"use client"

import type { Organization } from "better-auth/plugins/organization"
import { MenuDots as EllipsisIcon, Refresh as Loader2, Letter as MailIcon, Close as XIcon } from "@papier/ui/components/icons"
import { useContext, useMemo, useState } from "react"

import { AuthUIContext } from "../../lib/auth-ui-provider"
import { getLocalizedError } from "../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { Invitation } from "../../types/invitation"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { Button } from "@papier/ui/components/ui/button"
import { Card } from "@papier/ui/components/ui/card"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@papier/ui/components/ui/dropdown-menu"
import { UserAvatar } from "../user-avatar"

export interface InvitationCellProps {
    className?: string
    classNames?: SettingsCardClassNames
    invitation: Invitation
    localization?: AuthLocalization
    organization: Organization
}

export function InvitationCell({
    className,
    classNames,
    invitation,
    localization: localizationProp,
    organization
}: InvitationCellProps) {
    const {
        authClient,
        hooks: { useListInvitations, useHasPermission },
        organization: organizationOptions,
        localization: contextLocalization,
        toast
    } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const [isLoading, setIsLoading] = useState(false)

    const builtInRoles = [
        { role: "owner", label: localization.OWNER },
        { role: "admin", label: localization.ADMIN },
        { role: "member", label: localization.MEMBER }
    ]

    const roles = [...builtInRoles, ...(organizationOptions?.customRoles || [])]
    const role = roles.find((r) => r.role === invitation.role)

    const { refetch } = useListInvitations({
        query: { organizationId: organization?.id }
    })

    const { data: hasPermissionToCancel } = useHasPermission({
        organizationId: organization?.id,
        permissions: { invitation: ["cancel"] }
    })
    const { data: hasPermissionToResend } = useHasPermission({
        organizationId: organization.id,
        permissions: { invitation: ["create"] }
    })
    const expired = new Date(invitation.expiresAt) <= new Date()
    const handleResendInvitation = async () => {
        setIsLoading(true)
        try {
            await authClient.organization.inviteMember({
                email: invitation.email,
                role: invitation.role as "owner" | "admin" | "member",
                organizationId: organization.id,
                resend: true,
                fetchOptions: { throw: true }
            })
            await refetch?.()
            toast({ variant: "success", message: localization.INVITATION_RESENT })
        } catch (error) {
            toast({ variant: "error", message: getLocalizedError({ error, localization }) })
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancelInvitation = async () => {
        setIsLoading(true)

        try {
            await authClient.organization.cancelInvitation({
                invitationId: invitation.id,
                fetchOptions: { throw: true }
            })

            await refetch?.()

            toast({
                variant: "success",
                message: localization.INVITATION_CANCELLED
            })
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })
        }

        setIsLoading(false)
    }

    return (
        <Card
            className={cn(
                "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-4 sm:flex sm:flex-row",
                className,
                classNames?.cell
            )}
        >
            <div className="flex min-w-0 flex-1 items-center gap-2">
                <UserAvatar
                    className="my-0.5"
                    user={invitation}
                    localization={localization}
                />

                <div className="grid min-w-0 flex-1 text-left leading-tight">
                    <span className="break-all text-sm font-semibold">
                        {invitation.email}
                    </span>

                    <span className="text-muted-foreground truncate text-xs">
                        {expired ? localization.INVITATION_EXPIRED : `${localization.EXPIRES} ${new Date(invitation.expiresAt).toLocaleString()}`}
                    </span>
                </div>
            </div>

            <span className="col-start-1 pl-10 text-sm opacity-70 sm:pl-0">{role?.label}</span>

            {(hasPermissionToCancel?.success || hasPermissionToResend?.success) && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            className={cn(
                                "relative col-start-2 row-start-1 row-span-2 ms-auto min-h-11 min-w-11",
                                classNames?.button,
                                classNames?.outlineButton
                            )}
                            disabled={isLoading}
                            size="icon"
                            type="button"
                            variant="outline"
                            aria-label={`Invitation actions for ${invitation.email}`}
                        >
                            {isLoading ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                <EllipsisIcon className={classNames?.icon} />
                            )}
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                        align="end"
                    >
                        {hasPermissionToResend?.success && <DropdownMenuItem onClick={() => { void handleResendInvitation() }} disabled={isLoading}>
                            <MailIcon className={classNames?.icon} />
                            {localization.RESEND_INVITATION}
                        </DropdownMenuItem>}
                        {hasPermissionToCancel?.success && <DropdownMenuItem
                            onClick={() => { void handleCancelInvitation() }}
                            disabled={isLoading}
                            variant="destructive"
                        >
                            <XIcon className={classNames?.icon} />

                            {localization.CANCEL_INVITATION}
                        </DropdownMenuItem>}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </Card>
    )
}
